import React, { useState, useEffect } from 'react';
import { FaTrophy, FaChevronRight, FaInfoCircle, FaArrowRight, FaHeart, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface PathwayStep {
  step: number;
  title: string;
  description: string;
  level: string;
  color: string;
  tags: string[];
  icon: string;
}

const pathwaySteps: PathwayStep[] = [
  {
    step: 4,
    title: '🏠 Daily Life & Practical Skills',
    description: 'Essential phrases for daily activities and practical situations',
    level: 'Intermediate',
    color: 'purple',
    tags: ['daily life', 'practical', 'intermediate'],
    icon: '4'
  },
  {
    step: 3,
    title: '💬 Conversation & Culture',
    description: 'Practice everyday conversations and learn cultural context',
    level: 'Intermediate',
    color: 'yellow',
    tags: ['conversation', 'culture', 'intermediate'],
    icon: '3'
  },
  {
    step: 2,
    title: '📝 Grammar Fundamentals',
    description: 'Master essential Persian grammar rules and sentence structure',
    level: 'Beginner',
    color: 'green',
    tags: ['grammar', 'sentence structure', 'beginner'],
    icon: '2'
  },
  {
    step: 1,
    title: '🔤 Alphabet & Basics',
    description: 'Learn the Persian alphabet and fundamental pronunciation',
    level: 'Beginner',
    color: 'blue',
    tags: ['alphabet', 'basics', 'pronunciation', 'beginner'],
    icon: '1'
  },
  {
    step: 8,
    title: '🎭 Literature & Advanced Culture',
    description: 'Explore Persian poetry, literature, and advanced cultural topics',
    level: 'Advanced',
    color: 'pink',
    tags: ['literature', 'poetry', 'arts', 'culture'],
    icon: '8'
  },
  {
    step: 7,
    title: '💼 Business & Professional',
    description: 'Professional Persian for workplace and business situations',
    level: 'Advanced',
    color: 'gray',
    tags: ['business', 'work', 'office', 'professional'],
    icon: '7'
  },
  {
    step: 6,
    title: '✈️ Travel & Tourism',
    description: 'Essential phrases for traveling in Persian-speaking regions',
    level: 'Advanced',
    color: 'indigo',
    tags: ['travel', 'tourism', 'advanced'],
    icon: '6'
  },
  {
    step: 5,
    title: '🍽️ Food & Dining',
    description: 'Learn about Persian cuisine and dining etiquette',
    level: 'Intermediate',
    color: 'red',
    tags: ['food', 'dining', 'cuisine', 'intermediate'],
    icon: '5'
  }
];

// Enhanced Arrow Component
const CuteArrow: React.FC<{ isCompleted?: boolean }> = ({ isCompleted = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  const handleMouseEnter = () => {
    setIsHovered(true);
    setShowTooltip(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowTooltip(false);
  };
  
  return (
    <div 
      className="arrow-container cute-arrow relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background circle with gradient */}
      <div className={`
        arrow-background
        ${isCompleted 
          ? 'completed bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg shadow-green-200' 
          : 'progress bg-gradient-to-r from-indigo-400 to-purple-500 shadow-lg shadow-indigo-200'
        }
        ${isHovered ? 'scale-110 rotate-3' : 'scale-100 rotate-0'}
        hover:shadow-xl
        cursor-pointer
      `}>
        
        {/* Animated arrow icon */}
        <FaArrowRight 
          className={`
            arrow-icon text-white text-lg font-bold
            ${isHovered ? 'translate-x-1 scale-110' : 'translate-x-0 scale-100'}
          `}
        />
        
        {/* Floating particles effect */}
        {isHovered && (
          <>
            <div className="particle absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-bounce opacity-80"></div>
            <div className="particle absolute -bottom-1 -left-1 w-2 h-2 bg-pink-400 rounded-full animate-pulse opacity-80"></div>
            <div className="particle absolute -top-2 left-1/2 w-1.5 h-1.5 bg-blue-400 rounded-full animate-ping opacity-60"></div>
            <div className="particle absolute -bottom-2 right-1/2 w-1 h-1 bg-purple-400 rounded-full animate-bounce opacity-70"></div>
          </>
        )}
        
        {/* Glow effect on hover */}
        <div className={`
          absolute inset-0 rounded-full opacity-0 transition-opacity duration-300
          ${isCompleted 
            ? 'bg-gradient-to-r from-green-300 to-emerald-400' 
            : 'bg-gradient-to-r from-indigo-300 to-purple-400'
          }
          ${isHovered ? 'opacity-30' : 'opacity-0'}
        `}></div>
      </div>
      
      {/* Connection line with animation */}
      <div className="connection-line absolute w-16 h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent opacity-60"></div>
      
      {/* Cute emoji indicator */}
      {isCompleted && (
        <div className="status-indicator completed absolute -top-3 -right-3">
          <FaHeart className="text-red-400 text-sm animate-pulse" />
        </div>
      )}
      
      {/* Progress indicator */}
      {!isCompleted && (
        <div className="status-indicator progress absolute -bottom-2 left-1/2 transform -translate-x-1/2">
          <FaStar className="text-yellow-400 text-xs animate-spin" style={{ animationDuration: '3s' }} />
        </div>
      )}
      
      {/* Success sparkles for completed arrows */}
      {isCompleted && isHovered && (
        <>
          <div className="absolute -top-4 -left-2 text-green-400 text-xs animate-ping">✨</div>
          <div className="absolute -bottom-4 -right-2 text-green-400 text-xs animate-ping" style={{ animationDelay: '0.5s' }}>✨</div>
          <div className="absolute top-1/2 -left-4 text-green-400 text-xs animate-ping" style={{ animationDelay: '1s' }}>✨</div>
        </>
      )}
      
      {/* Interactive Tooltip */}
      {showTooltip && (
        <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 z-50">
          <div className={`
            bg-white rounded-lg shadow-lg px-3 py-2 text-sm font-medium whitespace-nowrap
            border-2 ${isCompleted ? 'border-green-200' : 'border-indigo-200'}
            animate-bounce
          `}>
            <div className="flex items-center gap-2">
              {isCompleted ? (
                <>
                  <span className="text-green-500">✅</span>
                  <span className="text-gray-700">Completed!</span>
                </>
              ) : (
                <>
                  <span className="text-indigo-500">🎯</span>
                  <span className="text-gray-700">Next step</span>
                </>
              )}
            </div>
            {/* Tooltip arrow */}
            <div className={`
              absolute top-full left-1/2 transform -translate-x-1/2
              w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent
              ${isCompleted ? 'border-t-green-200' : 'border-t-indigo-200'}
            `}></div>
          </div>
        </div>
      )}
      
      {/* Click ripple effect */}
      {isHovered && (
        <div className="absolute inset-0 rounded-full bg-white opacity-20 animate-ping"></div>
      )}
    </div>
  );
};

const PersianLearningPathway: React.FC = () => {
  const [completedSteps, setCompletedSteps] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pathwayProgress, setPathwayProgress] = useState<any[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchPathwayProgress();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  const fetchPathwayProgress = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/progress/pathway`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const { pathwayProgress, overallProgress } = response.data.data;
        setPathwayProgress(pathwayProgress);
        setCompletedSteps(overallProgress.completedSteps);
      }
    } catch (error) {
      console.error('Error fetching pathway progress:', error);
      // Fallback to localStorage if API fails
      const savedSteps = localStorage.getItem('completedSteps');
      if (savedSteps) {
        setCompletedSteps(parseInt(savedSteps));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateProgress = async (stepNumber: number, stepTitle: string, status: string) => {
    try {
      if (user) {
        // Update via API
        await axios.put(`${API_BASE_URL}/progress/pathway`, {
          stepNumber,
          stepTitle,
          status
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        // Refresh progress data
        await fetchPathwayProgress();
      } else {
        // Fallback to localStorage for non-authenticated users
        setCompletedSteps(stepNumber);
        localStorage.setItem('completedSteps', stepNumber.toString());
      }
      
      // Show completion modal if all steps are done
      if (stepNumber === 8) {
        setTimeout(() => {
          setShowCompletionModal(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Error updating pathway progress:', error);
      // Fallback to localStorage
      setCompletedSteps(stepNumber);
      localStorage.setItem('completedSteps', stepNumber.toString());
    }
  };

  const handleStepClick = async (stepNum: number) => {
    const step = pathwaySteps.find(s => s.step === stepNum);
    if (!step) return;

    // Determine the status based on current progress
    let status = 'not_started';
    if (stepNum <= completedSteps) {
      status = 'completed';
    } else if (stepNum === completedSteps + 1) {
      status = 'in_progress';
    }

    // Update progress
    await updateProgress(stepNum, step.title, status);

    // Navigate to the corresponding pathway
    const pathwayMap: { [key: number]: string } = {
      1: '/easy-persian-alphabet',
      2: '/pathways/6875e2c70e9af9bca4a60a0c',
      3: '/pathways/6875e2c70e9af9bca4a60a27',
      4: '/pathways/describing-daily-routines',
      5: '/pathways/6875e2c70e9af9bca4a60a2a',
      6: '/pathways/6875e2c70e9af9bca4a60a1e',
      7: '/pathways/6875e2c70e9af9bca4a60a21',
      8: '/pathways/6875e2c70e9af9bca4a60a15'
    };
    
    const pathwayUrl = pathwayMap[stepNum] || '/pathways';
    navigate(pathwayUrl);
  };

  const progressPercent = (completedSteps / 8) * 100;

  const getStepStatus = (stepNum: number) => {
    if (stepNum <= completedSteps) {
      return 'completed';
    } else if (stepNum === completedSteps + 1) {
      return 'current';
    } else {
      return 'locked';
    }
  };

  const getStepButton = (step: PathwayStep) => {
    const status = getStepStatus(step.step);
    
    switch (status) {
      case 'completed':
        const stepProgress = Math.round((step.step / 8) * 100);
        return (
          <div className="w-full">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">Progress</span>
              <span className="text-xs font-bold text-green-600">{stepProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full progress-fill"
                style={{ 
                  '--progress-width': `${stepProgress}%`,
                  width: '0%'
                } as React.CSSProperties}
              ></div>
            </div>
            <button 
              className="explore-btn px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-green-700 transition w-full"
              onClick={() => handleStepClick(step.step)}
            >
              📚 Learn More
            </button>
          </div>
        );
      case 'current':
        return (
          <button 
            className="explore-btn px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition w-full"
            onClick={() => handleStepClick(step.step)}
          >
            ▶ Start Learning
          </button>
        );
      default:
        return (
          <button 
            className="explore-btn px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-lg cursor-not-allowed w-full"
            disabled
          >
            🔒 Locked
          </button>
        );
    }
  };

  // Group pathway steps into rows of 4
  const groupedSteps = [];
  for (let i = 0; i < pathwaySteps.length; i += 4) {
    groupedSteps.push(pathwaySteps.slice(i, i + 4));
  }
  
  // Debug: Log the grouping to console
  console.log('Grouped Steps:', groupedSteps);

  return (
    <div className="min-h-screen font-sans">
      <div className="container mx-auto px-4 py-8 max-w-none">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-indigo-900 mb-3">🛤️ Persian Learning Pathway</h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Follow this structured learning path to master Persian step by step. Each stage builds upon the previous one.
          </p>
          
          {/* Progress Section */}
          <div className="mt-8 bg-white rounded-xl shadow-md p-6 max-w-2xl mx-auto">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-gray-700">Your Progress</span>
              {isLoading ? (
                <span className="font-bold text-indigo-600">Loading...</span>
              ) : (
                <span className="font-bold text-indigo-600">{completedSteps} / 8</span>
              )}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-full h-2.5 progress-fill"
                style={{ 
                  '--progress-width': `${progressPercent}%`,
                  width: '0%'
                } as React.CSSProperties}
              ></div>
            </div>
            <div className="mt-4 text-sm text-gray-500">
              <FaInfoCircle className="mr-1 text-indigo-400 inline" />
              {user ? 'Your real progress is tracked as you complete pathways.' : 'Sign in to track your progress across sessions.'}
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-center text-indigo-600 font-medium">
            <FaChevronRight className="mr-2 animate-pulse" />
            Start at Step 1 and follow the progression from left to right!
            <FaChevronRight className="ml-2 animate-pulse" />
          </div>
        </header>

        {/* Pathway Steps - 3 cards per row with horizontal arrows */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your progress...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8 mb-12 max-w-none mx-auto">
            {groupedSteps.map((row, rowIndex) => (
              <div key={rowIndex} className="flex items-center justify-end gap-4">
                {row.map((step, stepIndex) => (
                  <React.Fragment key={step.step}>
                    <div 
                      className={`pathway-card bg-white rounded-lg shadow-md overflow-hidden border-t-4 relative transition-all duration-300 hover:transform hover:-translate-y-1 hover:scale-105 flex-shrink-0 ${
                        getStepStatus(step.step) === 'locked' ? 'opacity-70' : ''
                      }`}
                      style={{ borderTopColor: `var(--${step.color}-500)` }}
                    >
                      <div className="p-6 w-64">
                        <div className="text-center mb-4">
                          <span className={`w-16 h-16 rounded-full text-white flex items-center justify-center mx-auto mb-3 text-2xl font-bold ${
                            step.color === 'blue' ? 'bg-blue-500' :
                            step.color === 'purple' ? 'bg-purple-500' :
                            step.color === 'amber' ? 'bg-amber-500' :
                            step.color === 'emerald' ? 'bg-emerald-500' :
                            step.color === 'red' ? 'bg-red-500' :
                            step.color === 'indigo' ? 'bg-indigo-500' :
                            step.color === 'gray' ? 'bg-gray-600' :
                            'bg-pink-500'
                          }`}>
                            {step.step}
                          </span>
                          <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                            step.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                            step.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                            step.color === 'amber' ? 'bg-amber-100 text-amber-800' :
                            step.color === 'emerald' ? 'bg-emerald-100 text-emerald-800' :
                            step.color === 'red' ? 'bg-red-100 text-red-800' :
                            step.color === 'indigo' ? 'bg-indigo-100 text-indigo-800' :
                            step.color === 'gray' ? 'bg-gray-100 text-gray-800' :
                            'bg-pink-100 text-pink-800'
                          }`}>
                            {step.level}
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-bold text-gray-800 mb-3 text-center leading-tight">
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 text-center line-clamp-3">{step.description}</p>
                        
                        <div className="flex flex-wrap gap-1 mb-4 justify-center">
                          {step.tags.slice(0, 3).map((tag, idx) => (
                            <span 
                              key={idx}
                              className={`tag text-xs px-2 py-1 rounded-full transition-all duration-200 hover:scale-105 ${
                                step.color === 'blue' ? 'bg-blue-50 text-blue-600' :
                                step.color === 'purple' ? 'bg-purple-50 text-purple-600' :
                                step.color === 'amber' ? 'bg-amber-50 text-amber-600' :
                                step.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
                                step.color === 'red' ? 'bg-red-50 text-red-600' :
                                step.color === 'indigo' ? 'bg-indigo-50 text-indigo-600' :
                                step.color === 'gray' ? 'bg-gray-50 text-gray-600' :
                                'bg-pink-50 text-pink-600'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        
                        {getStepButton(step)}
                      </div>
                    </div>
                    
                    {/* Enhanced Arrow between cards (except after the last card in each row) */}
                    {stepIndex < row.length - 1 && (
                      <div className="flex items-center px-2">
                        <CuteArrow isCompleted={getStepStatus(step.step) === 'completed'} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        )}
        
        {/* Completion Modal */}
        {showCompletionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md mx-4 text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaTrophy className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Congratulations!</h3>
              <p className="text-gray-600 mb-6">You've completed the Persian Learning Pathway! 🎉</p>
              <button 
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                onClick={() => setShowCompletionModal(false)}
              >
                Continue Learning
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersianLearningPathway;